import type { CountySources } from '@hotlist/shared';

/**
 * Where all this came from.
 *
 * DESIGN.md section 8 asks for this by name and describes exactly what it is
 * for: **three layers that never agree, and the disagreements are the whole
 * subject.**
 *
 * | layer | what it is | who it belongs to |
 * |---|---|---|
 * | world truth | what is actually out there | the server, and nobody else |
 * | the community map | what players have found and reported | the players |
 * | the archive snapshot | what the public record said on one date | this panel |
 *
 * The archive snapshot is dated and it stays dated. Cameras get taken, replaced
 * and resited, and after a year not one will be where the record said — and the
 * record still says it, because the record is a record. Section 8 is explicit
 * that this is a feature of the model rather than a defect in it.
 *
 * ## The part that is not decoration
 *
 * Section 6 names the vendor because the vendor is the subject of documented
 * criticism. A volunteer project that maps cameras is a different case: a small
 * group doing unpaid work who did not sign up to be depicted supplying tools to
 * whoever wants to cut a pole down. So the in-game map is its own thing, and
 * the real project is **credited here, as what this is drawn from**. That costs
 * nothing and it is the difference between citing somebody and volunteering
 * them.
 *
 * And the recursion worth noticing, which section 8 also insists on: two thirds
 * of the county's documented points came from volunteers mapping what they
 * happened to drive past. The archive's own file says the dataset "is not a
 * census, it is a floor". So the deployment this game is built on is biased
 * toward roads people drive — which is exactly the bias the in-game community
 * map has, for exactly the same reason.
 */

export class CitationsPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;

  constructor() {
    this.root = must('citations');
    this.body = must('citations-body');
    (must('citations-toggle') as HTMLButtonElement).addEventListener('click', () => {
      this.root.hidden = !this.root.hidden;
    });
    this.root.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).dataset.close !== undefined) this.root.hidden = true;
    });
  }

  show(sources: CountySources): void {
    this.body.replaceChildren();

    this.para(
      `The roads, road names, town names and posted speed limits are ` +
        `OpenStreetMap. The county is ${sources.county}, real, and its junctions are ` +
        `real junctions.`,
    );

    this.para(
      `${sources.cameraCount} camera positions, from a public archive snapshot ` +
        `taken ${sources.fetched}. That snapshot does not update. Cameras in this world get ` +
        `covered, broken, taken and resited, so after a while none of them will be where the ` +
        `record says — and the record will still say it.`,
    );

    this.table('where each position came from', sources.bySource);
    this.table('how well established each one was', sources.byConfidence);

    this.para(
      `Two thirds of those came from volunteers mapping what they happened to drive past, and ` +
        `the archive's own file calls the dataset a floor rather than a census. So the ` +
        `coverage here is biased toward roads people drive. The map players build for each ` +
        `other has the same bias, for the same reason.`,
    );

    this.credit();
  }

  private para(text: string): void {
    const p = document.createElement('p');
    p.className = 'citations-para';
    p.textContent = text;
    this.body.append(p);
  }

  private table(title: string, counts: Readonly<Record<string, number>>): void {
    const heading = document.createElement('div');
    heading.className = 'citations-heading';
    heading.textContent = title;
    this.body.append(heading);

    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    for (const [name, count] of entries) {
      const row = document.createElement('div');
      row.className = 'citations-row';
      const label = document.createElement('span');
      label.textContent = name;
      const value = document.createElement('span');
      value.className = 'citations-count';
      value.textContent = String(count);
      row.append(label, value);
      this.body.append(row);
    }
  }

  private credit(): void {
    const heading = document.createElement('div');
    heading.className = 'citations-heading';
    heading.textContent = 'drawn from';
    this.body.append(heading);

    // Named here and nowhere in the game itself. See the note at the top of
    // this file: crediting somebody is not the same as volunteering them.
    for (const [what, who] of [
      ['road network, names, speed limits', 'OpenStreetMap contributors'],
      ['camera positions and confidence tiers', 'WISCO Flock Watch'],
      ['much of the underlying survey work', 'DeFlock contributors, via OpenStreetMap'],
      ['811 locate filings and right-of-way permits', 'public records, as filed'],
      // Required by their terms of use, and worth saying plainly for its own
      // sake: everything above is a dated public record, and this is a
      // photograph of a day somebody flew over. They disagree, and the record
      // is the thing being played.
      ['aerial imagery, when it is turned on', 'Esri, Maxar, Earthstar Geographics'],
    ]) {
      const row = document.createElement('div');
      row.className = 'citations-credit';
      const label = document.createElement('span');
      label.textContent = what as string;
      const value = document.createElement('span');
      value.className = 'citations-who';
      value.textContent = who as string;
      row.append(label, value);
      this.body.append(row);
    }

    const note = document.createElement('p');
    note.className = 'citations-para';
    note.textContent =
      'The agencies and the vendor in this game are real. The stops, the searches and the ' +
      'people are not, and no real person appears anywhere in it.';
    this.body.append(note);
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
